import React, { createContext, useState, useContext, useEffect, useCallback, ReactNode } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { comuneConfigService } from '../services/comuneConfigService';
import type { ComunePublicConfig } from '../config/comuneConfig';

interface ConfigContextType {
  config: ComunePublicConfig;
  refresh: () => Promise<void>;
}

const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

// Module-level ref so non-React code (e.g. services) can read the merged
// config without a hook. ConfigProvider keeps this in sync on every fetch.
let _activeConfig: ComunePublicConfig | null = null;

export function getActiveComuneConfig(): ComunePublicConfig {
  if (!_activeConfig) {
    throw new Error('Comune config not loaded yet — call inside the app shell');
  }
  return _activeConfig;
}

export function getActiveDocumentPrefix(): string {
  return getActiveComuneConfig().documentPrefix;
}

export const useComuneConfig = (): ComunePublicConfig => {
  const ctx = useContext(ConfigContext);
  if (!ctx) {
    throw new Error('useComuneConfig must be used inside <ConfigProvider>');
  }
  return ctx.config;
};

export const useComuneConfigRefresh = (): (() => Promise<void>) => {
  const ctx = useContext(ConfigContext);
  if (!ctx) {
    throw new Error('useComuneConfigRefresh must be used inside <ConfigProvider>');
  }
  return ctx.refresh;
};

interface ConfigProviderProps {
  children: ReactNode;
}

export const ConfigProvider: React.FC<ConfigProviderProps> = ({ children }) => {
  const [config, setConfig] = useState<ComunePublicConfig | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(async () => {
    try {
      const next = await comuneConfigService.getPublicConfig();
      _activeConfig = next;
      document.title = `${next.displayName} gestione dati`;
      setConfig(next);
      setError(null);
    } catch (e) {
      setError(e as Error);
    }
  }, []);

  useEffect(() => {
    refresh();
    const interval = window.setInterval(refresh, 60_000);
    const onFocus = () => { void refresh(); };
    window.addEventListener('focus', onFocus);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [refresh]);

  if (!config) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress />
        {error && (
          <Typography color="error" variant="body2">
            Errore caricamento configurazione: {error.message}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <ConfigContext.Provider value={{ config, refresh }}>
      {children}
    </ConfigContext.Provider>
  );
};
